import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pause, Play, ArrowsOut, X } from "@phosphor-icons/react";
import { useMiniPlayer } from "@/context/MiniPlayerContext";
import { useTvMode, isTvUserAgent } from "@/context/TvModeContext";
import { useVideoEnd, withJsApi, getYtPlayer } from "@/hooks/useAutoplayNext";
import { saveProgress } from "@/lib/progress";

const MINI_ID = "rv-mini-iframe";
const ctrl = "flex h-9 w-9 items-center justify-center rounded-full bg-black/70 border border-white/25 text-white backdrop-blur-md transition-colors duration-200 hover:bg-amber hover:text-black hover:border-amber focus:outline-none focus:ring-2 focus:ring-amber";

// Floating bottom-right player that keeps a video going while the viewer browses.
export const MiniPlayer = () => {
  const { mini, closeMini } = useMiniPlayer();
  const { tvMode } = useTvMode();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(true);
  const timeRef = useRef(0);
  const onWatch = /^\/(watch|jw-test)\/|^\/shorts\/feed/.test(pathname);
  const active = !!mini && !tvMode && !isTvUserAgent() && !onWatch;

  useEffect(() => {
    if (mini) {
      timeRef.current = mini.start || 0;
      setPlaying(true);
    }
  }, [mini]);

  useEffect(() => {
    if (mini && (tvMode || onWatch)) closeMini();
  }, [mini, tvMode, onWatch, closeMini]);

  const src = useMemo(
    () => (mini ? `${withJsApi(mini.embedUrl)}&autoplay=1&playsinline=1&start=${Math.floor(mini.start || 0)}` : ""),
    [mini]
  );

  useVideoEnd({
    videoId: mini?.video_id,
    embedUrl: mini?.embedUrl,
    durationSec: mini?.duration,
    iframeId: MINI_ID,
    enabled: active,
    onEnd: () => setPlaying(false),
    onProgress: (ct, du) => {
      timeRef.current = ct;
      saveProgress({ video_id: mini.video_id, timestamp: ct, duration: du, title: mini.title, thumb: mini.thumb });
    },
  });

  if (!active) return null;

  const send = (func) =>
    document.getElementById(MINI_ID)?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");
  const togglePlay = () => {
    const p = getYtPlayer(MINI_ID);
    const isPlaying = p?.getPlayerState ? p.getPlayerState() === 1 : playing;
    send(isPlaying ? "pauseVideo" : "playVideo");
    setPlaying(!isPlaying);
  };
  // Stop audio first, then unmount (removes the iframe and destroys the YT player).
  const close = (e) => {
    e?.stopPropagation();
    send("stopVideo");
    closeMini();
  };
  const expand = () => {
    const t = Math.floor(getYtPlayer(MINI_ID)?.getCurrentTime?.() || timeRef.current || 0);
    const id = mini.video_id;
    closeMini();
    navigate(`/watch/${id}?t=${t}&autoplay=1`);
  };

  return (
    <div
      data-testid="mini-player"
      className="rv-mini fixed bottom-4 right-4 z-[95] w-[min(88vw,340px)] sm:w-[380px] overflow-hidden border border-white/15 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
    >
      <div className="group relative aspect-video w-full">
        <iframe
          key={`${mini.video_id}-${mini.nonce}`}
          id={MINI_ID}
          data-testid="mini-player-iframe"
          src={src}
          title={mini.title}
          className="absolute inset-0 h-full w-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
        <div className="rv-mini-controls pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-end gap-1.5 bg-gradient-to-b from-black/70 to-transparent p-2">
          <button type="button" data-testid="mini-player-toggle" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} className={`pointer-events-auto ${ctrl}`}>
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
          </button>
          <button type="button" data-testid="mini-player-expand" onClick={expand} aria-label="Expand player" className={`pointer-events-auto ${ctrl}`}>
            <ArrowsOut size={16} weight="bold" />
          </button>
          <button type="button" data-testid="mini-player-close" onClick={close} aria-label="Close player" className={`pointer-events-auto ${ctrl} hover:bg-crimson hover:border-crimson hover:text-white`}>
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
      <p data-testid="mini-player-title" className="truncate border-t border-white/10 px-3 py-2 text-[12px] text-white/80">{mini.title}</p>
    </div>
  );
};
